import React from 'react';
import { Box, Paper, Skeleton, Stack, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import PlaceRoundedIcon from '@mui/icons-material/PlaceRounded';
import RestaurantRoundedIcon from '@mui/icons-material/RestaurantRounded';
import RouteRoundedIcon from '@mui/icons-material/RouteRounded';

type PlannerLoadingVariant = 'activities' | 'restaurants';

interface PlannerLoadingPlaceholderProps {
  variant?: PlannerLoadingVariant;
  phase?: string | null;
}

const getPlaceholderCopy = (variant: PlannerLoadingVariant, phase?: string | null) => {
  if (variant === 'restaurants') {
    return {
      eyebrow: 'Dining picks loading',
      title: 'Finding places worth your meals',
      description: 'Matching restaurants with your route, timing, and local food options.',
      steps: ['Local favorites', 'Meal timing', 'Neighborhood fit'],
      icon: <RestaurantRoundedIcon />,
    };
  }

  if (phase === 'activities_preliminary') {
    return {
      eyebrow: 'Details loading',
      title: 'Polishing your itinerary cards',
      description: 'Checking activity details, visit timing, and image options for the first draft.',
      steps: ['Activity details', 'Hours and timing', 'Local context'],
      icon: <AutoAwesomeRoundedIcon />,
    };
  }

  return {
    eyebrow: 'Plan generation',
    title: 'Building the first itinerary draft',
    description: 'Selecting anchor stops, shaping each day, and preparing your activity cards.',
    steps: ['Trip shape', 'Daily flow', 'Activity cards'],
    icon: <RouteRoundedIcon />,
  };
};

const cardImageWidths = ['82%', '64%', '74%'];

export const PlannerLoadingPlaceholder: React.FC<PlannerLoadingPlaceholderProps> = ({
  variant = 'activities',
  phase,
}) => {
  const theme = useTheme();
  const accent = variant === 'restaurants' ? '#FF6B6B' : theme.palette.secondary.main;
  const copy = getPlaceholderCopy(variant, phase);

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2.5, sm: 3, md: 3.5 },
        borderRadius: 4,
        border: `1px solid ${alpha(theme.palette.primary.main, 0.14)}`,
        background: `
          linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.98)} 0%, ${alpha(theme.palette.background.paper, 0.92)} 100%),
          linear-gradient(90deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.secondary.main, 0.08)})
        `,
        boxShadow: `0 22px 54px ${alpha(theme.palette.common.black, 0.06)}`,
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      <Stack spacing={{ xs: 2.5, md: 3 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: theme.palette.common.white,
                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${accent})`,
                boxShadow: `0 14px 28px ${alpha(accent, 0.2)}`,
                '& svg': { fontSize: 23 },
              }}
            >
              {copy.icon}
            </Box>
            <Box>
              <Typography variant="overline" sx={{ color: 'primary.main', fontWeight: 700 }}>
                {copy.eyebrow}
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 750, lineHeight: 1.2 }}>
                {copy.title}
              </Typography>
            </Box>
          </Stack>
          <Typography
            variant="body2"
            sx={{ color: 'text.secondary', maxWidth: 390, textAlign: { xs: 'left', sm: 'right' } }}
          >
            {copy.description}
          </Typography>
        </Stack>

        <Box>
          <Stack direction="row" spacing={1} sx={{ mb: 1.25, flexWrap: 'wrap', rowGap: 1 }}>
            {copy.steps.map((step, index) => (
              <Box
                key={step}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.75,
                  px: 1.25,
                  py: 0.55,
                  borderRadius: '999px',
                  color: index === 0 ? theme.palette.primary.dark : theme.palette.text.secondary,
                  backgroundColor: index === 0 ? alpha(theme.palette.primary.main, 0.1) : alpha(theme.palette.common.white, 0.78),
                  border: `1px solid ${index === 0 ? alpha(theme.palette.primary.main, 0.18) : alpha(theme.palette.divider, 0.8)}`,
                  fontSize: '0.78rem',
                  fontWeight: 650,
                }}
              >
                {index === 0 ? <PlaceRoundedIcon sx={{ fontSize: 15 }} /> : null}
                {step}
              </Box>
            ))}
          </Stack>
          <Box
            sx={{
              height: 6,
              borderRadius: '999px',
              backgroundColor: alpha(accent, 0.12),
              overflow: 'hidden',
              position: 'relative',
              '&::after': {
                content: '""',
                position: 'absolute',
                inset: 0,
                width: '42%',
                borderRadius: '999px',
                background: `linear-gradient(90deg, transparent, ${accent}, transparent)`,
                animation: 'plannerPlaceholderSweep 1.8s ease-in-out infinite',
              },
              '@keyframes plannerPlaceholderSweep': {
                '0%': { transform: 'translateX(-110%)' },
                '100%': { transform: 'translateX(250%)' },
              },
            }}
          />
        </Box>

        <Box
          sx={{
            display: 'grid',
            gap: { xs: 2, md: 2.5 },
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, minmax(0, 1fr))',
              xl: 'repeat(3, minmax(0, 1fr))',
            },
          }}
        >
          {[0, 1, 2].map((cardIndex) => (
            <Box
              key={cardIndex}
              sx={{
                minHeight: { xs: 248, md: 286 },
                borderRadius: 2,
                overflow: 'hidden',
                border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                backgroundColor: alpha(theme.palette.background.paper, 0.84),
                boxShadow: `0 16px 34px ${alpha(theme.palette.common.black, 0.06)}`,
              }}
            >
              <Skeleton
                variant="rectangular"
                animation="wave"
                sx={{
                  height: { xs: 132, md: 150 },
                  bgcolor: alpha(accent, 0.12),
                }}
              />
              <Stack spacing={1.25} sx={{ p: 2 }}>
                <Skeleton animation="wave" height={28} width={cardImageWidths[cardIndex]} />
                <Skeleton animation="wave" height={24} width="44%" sx={{ borderRadius: '999px' }} />
                <Stack direction="row" spacing={0.8} sx={{ flexWrap: 'wrap', rowGap: 0.75 }}>
                  <Skeleton animation="wave" height={23} width={76} sx={{ borderRadius: '999px' }} />
                  <Skeleton animation="wave" height={23} width={92} sx={{ borderRadius: '999px' }} />
                  <Skeleton animation="wave" height={23} width={68} sx={{ borderRadius: '999px' }} />
                </Stack>
              </Stack>
            </Box>
          ))}
        </Box>
      </Stack>
    </Paper>
  );
};
